import React from 'react';
import {
  IconButton as MuiIconButton,
  IconButtonProps as MuiIconButtonProps,
} from '@mui/material';
import { alpha, SxProps, Theme } from '@mui/material/styles';

export type IconButtonTone =
  | 'neutral'
  | 'primary'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger';
export type IconButtonVariant = 'ghost' | 'outlined' | 'filled' | 'overlay';
export type IconButtonDensity = 'compact' | 'regular';

export interface IconButtonProps extends Omit<MuiIconButtonProps, 'color'> {
  density?: IconButtonDensity;
  tone?: IconButtonTone;
  variant?: IconButtonVariant;
}

const baseStyles = {
  flex: '0 0 auto',
  border: '1px solid transparent',
  borderRadius: '50%',
  transition:
    'background-color 140ms ease, border-color 140ms ease, color 140ms ease',
};
const regularStyles = {
  width: 38,
  height: 38,
  '& > svg': { fontSize: '1.25rem' },
};
const compactStyles = {
  width: 34,
  height: 34,
  '& > svg': { fontSize: '1.125rem' },
};

const getToneColor = (theme: Theme, tone: IconButtonTone) => {
  if (tone === 'neutral') {
    return theme.palette.text.secondary;
  }

  if (tone === 'danger') {
    return theme.palette.error.main;
  }

  return theme.palette[tone].main;
};

const getVariantStyles = (
  theme: Theme,
  tone: IconButtonTone,
  variant: IconButtonVariant
) => {
  const color = getToneColor(theme, tone);

  if (variant === 'overlay') {
    return {
      color: theme.palette.common.white,
      bgcolor: 'rgba(31, 34, 40, 0.88)',
      backdropFilter: 'blur(8px)',
      '&:hover': {
        color: theme.palette.common.white,
        bgcolor:
          tone === 'danger'
            ? theme.palette.error.main
            : 'rgba(49, 52, 60, 0.96)',
      },
    };
  }

  if (variant === 'filled') {
    return {
      color,
      bgcolor: alpha(color, 0.16),
      '&:hover': { bgcolor: alpha(color, 0.26) },
    };
  }

  if (variant === 'outlined') {
    return {
      color: tone === 'neutral' ? theme.palette.text.primary : color,
      bgcolor: 'transparent',
      borderColor: theme.palette.softBorder,
      '&:hover': {
        bgcolor: alpha(color, 0.12),
        borderColor: alpha(color, 0.62),
      },
    };
  }

  if (tone === 'danger') {
    return {
      color: theme.palette.text.primary,
      bgcolor: 'transparent',
      '&:hover': {
        color: theme.palette.error.contrastText,
        bgcolor: theme.palette.error.main,
      },
    };
  }

  return {
    color,
    bgcolor: 'transparent',
    '&:hover': { bgcolor: alpha(color, 0.12) },
  };
};

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      density = 'regular',
      tone = 'neutral',
      variant = 'outlined',
      size = 'small',
      sx,
      ...props
    },
    ref
  ) => {
    const variantStyles = (theme: Theme) =>
      getVariantStyles(theme, tone, variant);
    const combinedStyles = [
      baseStyles,
      density === 'compact' ? compactStyles : regularStyles,
      variantStyles,
      sx,
    ].filter(Boolean) as SxProps<Theme>;

    return (
      <MuiIconButton {...props} ref={ref} size={size} sx={combinedStyles} />
    );
  }
);

IconButton.displayName = 'IconButton';

export { IconButton };
